import { zodResolver } from '@hookform/resolvers/zod'
import { cn } from '@repo/tailwind-config/tailwind.functions'
import { useMutation } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { z, ZodType } from 'zod'

import {
  createTariffAdjustment,
  TariffAdjustmentInput,
  TariffAdjustmentTypeEnum,
  tariffAdjustmentTypeEnumMap
} from '../../../../api/enrollments/create-tariff-adjustment'
import { modalButtonConfig } from '../../../../lib/utils/buttons-config/validation-buttons-config'
import { showAxiosError } from '../../../../lib/utils/functions/misc.functions'
import { createNumberSchema } from '../../../../lib/utils/validations/int-array-validation'
import { createStringSchema } from '../../../../lib/utils/validations/string-validation'
import { FORM_MODE } from '../../../../utils/constants/form.constants'
import { Form, FormControl, FormField, FormItem } from '../../../elements/forms/form'
import ControlledInput from '../../../elements/inputs/controlled-input'
import ControlledRadioInput from '../../../elements/inputs/controlled-radio-input'
import ValidationButtons from '../../../elements/validation-buttons'

const TARIFF_ADJUSTMENT_TYPE_OPTIONS = Object.values(TariffAdjustmentTypeEnum).map((value) => ({
  value,
  label: tariffAdjustmentTypeEnumMap[value]
}))

const formSchema: ZodType<TariffAdjustmentInput> = z.object({
  type: z.nativeEnum(TariffAdjustmentTypeEnum, { required_error: 'Type requis' }),
  amount: createNumberSchema('Montant'),
  reason: createStringSchema('Motif')
})

type Props = {
  enrollmentId: number
  propositionId: number
  className?: string
  onSuccess: () => void
  onCancel: () => void
}

const TariffAdjustmentForm: React.FC<Props> = ({
  enrollmentId,
  propositionId,
  className,
  onSuccess,
  onCancel
}) => {
  const form = useForm<TariffAdjustmentInput>({
    resolver: zodResolver(formSchema),
    mode: FORM_MODE
  })

  const { mutateAsync, isPending } = useMutation({
    mutationFn: (data: TariffAdjustmentInput) =>
      createTariffAdjustment({ enrollmentId, propositionId, ...data }),
    onError: showAxiosError,
    onSuccess
  })

  const buttonsConfig = modalButtonConfig<TariffAdjustmentInput>({
    confirmText: 'Appliquer',
    cancelText: 'Annuler',
    onConfirm: (data) => mutateAsync(data),
    onCancel,
    isLoading: isPending
  })

  return (
    <Form {...form}>
      <form
        className={cn('flex flex-col', className)}
        onSubmit={form.handleSubmit((data) => mutateAsync(data))}
      >
        <div className="flex flex-col gap-4 p-4">
          <FormField
            control={form.control}
            name="type"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <div className="flex gap-4">
                    {TARIFF_ADJUSTMENT_TYPE_OPTIONS.map((option) => (
                      <ControlledRadioInput
                        key={option.value}
                        label={option.label}
                        checked={field.value === option.value}
                        onCheck={() => field.onChange(option.value)}
                      />
                    ))}
                  </div>
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="amount"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <ControlledInput type="number" {...field} label="Montant (€)" required />
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="reason"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <ControlledInput type="text" {...field} label="Motif" required />
                </FormControl>
              </FormItem>
            )}
          />
        </div>

        <ValidationButtons buttonsConfig={buttonsConfig} hasBorderTop />
      </form>
    </Form>
  )
}

export default TariffAdjustmentForm
